import { useEffect, useState } from "react";
import { useCountry } from "@/contexts/CountryContext";
import { COUNTRY_BY_CODE, flagEmoji } from "@/data/countries";
import { http } from "@/lib/api";
import { X } from "lucide-react";

const DISMISS_KEY = "wofy.country.autodetect.dismissed";

// While the platform is still building up country-tagged content we default
// every visitor to the Global view. The dismissible auto-detect prompt is
// disabled — flip this to `true` (or remove the guard) once each country
// has enough content that a country-first experience is worth defaulting to.
const AUTO_DETECT_ENABLED = false;

/**
 * On first visit (no country chosen and not previously dismissed) detect the
 * user's country via a lightweight server-side IP lookup (avoids client CORS)
 * and offer a subtle dismissible pill.
 */
export default function CountryAutoDetect() {
  const { code, setCode } = useCountry();
  const [detected, setDetected] = useState(null);
  const [dismissed, setDismissed] = useState(() => {
    try { return localStorage.getItem(DISMISS_KEY) === "1"; } catch { return false; }
  });

  useEffect(() => {
    if (!AUTO_DETECT_ENABLED) return;
    if (code || dismissed) return;
    let cancelled = false;
    (async () => {
      try {
        const { data } = await http.get("/geo/lookup", { timeout: 5000 });
        const cc = (data?.country_code || "").toUpperCase();
        if (!cancelled && cc && COUNTRY_BY_CODE[cc]) setDetected(cc);
      } catch {
        // Silently fall back — user can still pick manually.
      }
    })();
    return () => { cancelled = true; };
  }, [code, dismissed]);

  if (!AUTO_DETECT_ENABLED) return null;
  if (code || dismissed || !detected) return null;

  const c = COUNTRY_BY_CODE[detected];
  const dismiss = () => {
    setDismissed(true);
    try { localStorage.setItem(DISMISS_KEY, "1"); } catch { /* ignore */ }
  };

  return (
    <div
      role="dialog"
      aria-label="Set detected country"
      data-testid="country-autodetect-pill"
      className="fixed bottom-6 left-6 z-40 max-w-[360px] rounded-2xl border shadow-lg p-4 flex items-start gap-3 animate-in fade-in slide-in-from-bottom-4"
      style={{ background: "var(--wofy-paper)", borderColor: "var(--wofy-border)" }}
    >
      <div className="text-2xl leading-none" aria-hidden>{flagEmoji(detected)}</div>
      <div className="flex-1 min-w-0">
        <div className="text-sm">
          Looks like you're in <strong>{c.name}</strong>. Show content for {c.name}?
        </div>
        <div className="mt-3 flex gap-2">
          <button
            onClick={() => { setCode(detected); dismiss(); }}
            data-testid="country-autodetect-accept"
            className="wofy-btn-primary text-xs py-1.5 px-3"
          >
            Yes, set as default
          </button>
          <button
            onClick={dismiss}
            data-testid="country-autodetect-decline"
            className="wofy-btn-ghost text-xs py-1.5 px-3"
          >
            Not now
          </button>
        </div>
      </div>
      <button
        onClick={dismiss}
        aria-label="Dismiss"
        data-testid="country-autodetect-dismiss"
        className="opacity-50 hover:opacity-100"
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
}
